import { FunctionComponent, ReactNode, useCallback, useMemo } from 'react'

import styled, { css, useTheme } from 'styled-components'

import { desktopCSS } from '@measures/responsive'
import { triggerAureusDeboosting } from '@utils/aureus'
import { printCSS } from '@utils/style'
import AureusDeboostContext, {
  AureusDeboostContextType,
} from '@contexts/aureusDeboostContext'
import RecommendationTeaserContext from '@contexts/recommendationTeaser'
import usePageMetadata from '@hooks/usePageMetadata'
import { useRecommendations } from '@hooks/useRecommendations'
import useRecommendationsAdIndices from '@hooks/useRecommendationsAdIndices'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import JSONRenderer from '@components/JSONRenderer'
import SectionHeader from '@components/SectionHeader'

export interface RecommendationsWidgetAPIProps {
  id: 'recommendations-top-container' | 'recommendations-bottom-container'
  title: string
}

const RecommendationsWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16, spacing24, spacing32 },
    },
  }) => css`
    display: grid;
    grid-gap: ${spacing16};

    grid-template-columns: minmax(0, 1fr);

    ${printCSS(css`
      display: none;
    `)}

    ${desktopCSS(css`
      grid-row-gap: ${spacing24};
      grid-column-gap: ${spacing32};
      grid-template-columns: repeat(2, minmax(0, 1fr));

      > *:first-child {
        grid-column: 1 / -1;
      }
    `)}
  `}
`

const StyledYaleoAd = styled.div<{ isDoubleSlot?: boolean }>`
  ${({ isDoubleSlot }) => css`
    ${desktopCSS(css`
      ${isDoubleSlot &&
      css`
        grid-column: span 2;
      `}
    `)};
  `}
`

const Recommendations: FunctionComponent<RecommendationsWidgetAPIProps> = ({
  title,
  id,
}) => {
  const theme = useTheme()
  const { url } = usePageMetadata()

  const adElementIndices = useRecommendationsAdIndices()
  const { data: recommendations } = useRecommendations()
  const isTopContainer = id === 'recommendations-top-container'

  const aureusDeboostContextValue = useMemo<AureusDeboostContextType>(
    () => ({
      deboostOnClick: ({ aureusOfferId, articleId }) => {
        if (!aureusOfferId || !articleId) {
          return
        }
        triggerAureusDeboosting({
          itemsToDeboost: [{ offerId: aureusOfferId, contentId: articleId }],
        })
      },
    }),
    [],
  )

  const recommendationContextValue = useMemo(
    () => ({ isRecommendation: true, placement: id }),
    [id],
  )

  const onImpressionTracking = useCallback<TrackingFnType>(
    () => ({
      event: 'content_impression',
      placement: `article-${id}`,
      link_id: url,
    }),
    [id, url],
  )

  const onImpression = useTracking(onImpressionTracking)

  const viewportRef = useViewportTracking({
    onImpression,
    track: true,
  })

  const enrichedRecommendationElements = useMemo(() => {
    const isDoubleSlot =
      adElementIndices.length > 0 &&
      adElementIndices.length < 2 &&
      adElementIndices[0] % 2 === 0

    const validIndicesCount = isDoubleSlot
      ? adElementIndices[0] >= 0
        ? 2
        : 0
      : (adElementIndices[0] >= 0 ? 1 : 0) +
        ((adElementIndices as [number, number])[1] >= 0 ? 1 : 0)

    const filteredRecommendations = recommendations.slice(
      0,
      recommendations.length - validIndicesCount,
    )

    const enrichedRecommendationElements: ReactNode[] =
      filteredRecommendations.map((recommendedItem) => {
        return (
          <JSONRenderer key={recommendedItem.articleId}>
            {{ ...recommendedItem, track: true }}
          </JSONRenderer>
        )
      })

    if (adElementIndices[0] >= 0) {
      enrichedRecommendationElements.splice(
        adElementIndices[0],
        0,
        <StyledYaleoAd
          id={isDoubleSlot ? `yaleo-ad-double` : `yaleo-ad-1`}
          key={
            isDoubleSlot
              ? `yaleo-ad-double-config-[${adElementIndices[0]}]`
              : `yaleo-ad-1-config-[${adElementIndices[0]}, ${adElementIndices[1]}]`
          }
          isDoubleSlot={isDoubleSlot}
        />,
      )
    }

    if (!isDoubleSlot && (adElementIndices as [number, number])[1] >= 0) {
      enrichedRecommendationElements.splice(
        (adElementIndices as [number, number])[1],
        0,
        <StyledYaleoAd
          id={`yaleo-ad-2`}
          key={`yaleo-ad-2-config-[${adElementIndices[0]}, ${adElementIndices[1]}]`}
        />,
      )
    }

    return enrichedRecommendationElements
  }, [adElementIndices, recommendations])

  if (!recommendations || recommendations?.length === 0) {
    return null
  }

  if (!isTopContainer) {
    return null
  }

  return (
    <AureusDeboostContext.Provider value={aureusDeboostContextValue}>
      <RecommendationTeaserContext.Provider value={recommendationContextValue}>
        <RecommendationsWrapper ref={viewportRef}>
          <SectionHeader
            title={title}
            accentColor={theme.colors.strokeBrand}
            isSubheader={true}
          />
          {enrichedRecommendationElements}
        </RecommendationsWrapper>
      </RecommendationTeaserContext.Provider>
    </AureusDeboostContext.Provider>
  )
}

const widget = {
  kind: ['recommendations'],
  component: Recommendations,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
